import type { QueryClient } from '@tanstack/react-query';
import i18n from '../i18n';
import { api } from '../services/api';
import { toast } from '../store/toastStore';
import { apiErrorMessage } from '../components/sales/shared';
import { undoableDelete } from './undoableActions';

/**
 * P-27 (D-166 s.11) - one helper per deletable entity: hide at once, DELETE
 * after 6 s, "בטל" cancels. All of them are HARD deletes on the server (or have
 * side effects a restore could not bring back - a contact's portal grants), so
 * the request itself is delayed. Soft archives with a restore route use
 * `undoableArchive` in the page.
 *
 * `kind` is the key lists filter on: `useWithoutHidden(kind, rows)`.
 */
export const DELETE_KINDS = {
  ticket: 'ticket',
  milestone: 'milestone',
  project: 'project',
  meeting: 'meeting',
  contact: 'contact',
  lead: 'lead',
  engagement: 'engagement',
  workLog: 'work_log',
  attendancePolicy: 'attendance_policy',
} as const;

interface Common {
  qc: QueryClient;
  /** Runs when the delete was undone or failed (e.g. go back to the item's page). */
  onRestore?: () => void;
  /** Runs once the delete reached the server. */
  onCommitted?: () => void;
  /** The delayed request failed (default: an error toast). The row is already back. */
  onError?: (error: unknown) => void;
}

function invalidateAll(qc: QueryClient, keys: string[]) {
  keys.forEach((k) => { void qc.invalidateQueries({ queryKey: [k] }); });
}

function make(kind: string, id: string, message: string, run: () => Promise<unknown>, keys: string[], o: Common, unloadPath: string, failKey = 'undoToast.failed', unloadBody?: unknown) {
  return undoableDelete({
    kind, id, message, run,
    unload: { method: 'DELETE', path: unloadPath, ...(unloadBody !== undefined ? { body: unloadBody } : {}) },
    onCommitted: () => { invalidateAll(o.qc, keys); o.onCommitted?.(); },
    onRestore: () => { invalidateAll(o.qc, keys); o.onRestore?.(); },
    onError: o.onError ?? ((err) => toast('error', apiErrorMessage(err, i18n.t(failKey)))),
  });
}

const TICKET_KEYS = ['tickets', 'all-tasks', 'project', 'milestone', 'milestones', 'tasks', 'home'];

export function undoableDeleteTicket(o: Common & { id: string; taskType?: string | null; isConversation?: boolean }) {
  // FIX-B23 (QX-342): a conversation is a discussion row with is_conversation; its toast says "שיחה".
  const key = o.taskType === 'discussion' && o.isConversation ? 'undoToast.conversationDeleted'
    : o.taskType === 'discussion' ? 'undoToast.discussionDeleted' : o.taskType === 'task' ? 'undoToast.taskDeleted' : 'undoToast.ticketDeleted';
  return make(DELETE_KINDS.ticket, o.id, i18n.t(key), () => api.deleteTicket(o.id), TICKET_KEYS, o, `/tickets/${o.id}`);
}

export function undoableDeleteMilestone(o: Common & { id: string }) {
  return make(DELETE_KINDS.milestone, o.id, i18n.t('undoToast.milestoneDeleted'), () => api.deleteMilestone(o.id),
    ['milestones', 'all-milestones', 'milestone', 'project', 'tickets', 'all-tasks'], o, `/milestones/${o.id}`);
}

export function undoableDeleteProject(o: Common & { id: string }) {
  return make(DELETE_KINDS.project, o.id, i18n.t('undoToast.projectDeleted'), () => api.deleteProject(o.id),
    ['projects', 'project', 'engagements', 'organizations'], o, `/projects/${o.id}`);
}

export function undoableDeleteMeeting(o: Common & { id: string }) {
  return make(DELETE_KINDS.meeting, o.id, i18n.t('undoToast.meetingDeleted'), () => api.deleteMeeting(o.id), ['meetings'], o, `/meetings/${o.id}`);
}

/** A contact's delete revokes its portal grants (not restorable), so the request itself is delayed. */
export function undoableDeleteContact(o: Common & { id: string }) {
  return make(DELETE_KINDS.contact, o.id, i18n.t('undoToast.contactDeleted'), () => api.deleteContact(o.id),
    ['contacts', 'contact', 'portalAccess', 'organization'], o, `/contacts/${o.id}`);
}

export function undoableDeleteEngagement(o: Common & { id: string; hard?: boolean }) {
  return make(DELETE_KINDS.engagement, o.id, i18n.t('undoToast.engagementDeleted'), () => api.deleteEngagement(o.id, o.hard ?? true),
    ['engagements', 'engagement', 'projects', 'organizations'], o, `/engagements/${o.id}${(o.hard ?? true) ? '?hard=true' : ''}`);
}

/** P-46: only an EMPTY lead is deleted (hard, on the server), so the request is delayed (nothing to restore). */
export function undoableDeleteLead(o: Common & { id: string }) {
  return make(DELETE_KINDS.lead, o.id, i18n.t('undoToast.leadDeleted'), () => api.deleteLead(o.id), ['leads', 'lead'], o, `/leads/${o.id}`);
}

/**
 * FIX-B9 (P-27 left for the P-26 merge) - a work log ("רשומות עבודה"). Soft on
 * the server (`deleted_at` + an override reason inside a locked month), but no
 * restore route, so the request itself is delayed. A locked month answers
 * PERIOD_LOCKED_REASON_REQUIRED: the row comes back and the page asks for the reason.
 */
export function undoableDeleteWorkLog(o: Common & { id: string; reason?: string }) {
  return make(DELETE_KINDS.workLog, o.id, i18n.t('undoToast.workLogDeleted'), () => api.deleteWorkLog(o.id, o.reason),
    ['work-logs', 'work-records', 'timer-status', 'home'], o, `/work-logs/${o.id}`, 'undoToast.failed',
    o.reason ? { override_reason: o.reason } : undefined);
}

/** FIX-B9 - an attendance policy (admin, "נוכחות"): a hard delete. */
export function undoableDeleteAttendancePolicy(o: Common & { id: string }) {
  return make(DELETE_KINDS.attendancePolicy, o.id, i18n.t('undoToast.attendancePolicyDeleted'), () => api.deleteAttendancePolicy(o.id),
    ['attendance'], o, `/attendance/policies/${o.id}`);
}
